import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';

import { StepperAccordionSummary } from '@klareospace/pharos-lib-web-utils';
import type { StepperAccordionSummaryItem } from '@klareospace/pharos-lib-web-utils';

import { useCarriersForSelect } from '&/features/settings/carriers/hooks';

import { formatSchedulingTimeSlotLabel } from '../utils';
import type { OrderCreationFormInput } from '../schemas/order-creation.schema';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Collapsed "Scheduling" section recap. Owns its own `useWatch` on `scheduling` (plus the pickup
 * site, to scope the carrier list the same way the section body does) so a Scheduling keystroke
 * only re-renders this leaf instead of the whole drawer.
 */
export function SchedulingSectionSummary(): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { i18n, t } = useTranslation('order-creation');
  const { control } = useFormContext<OrderCreationFormInput>();
  const { carrierCode, deliveryTimeSlot } = useWatch({ control, name: 'scheduling' });
  const selectedSite = useWatch({ control, name: 'routeInformation.selectedSite' });
  const carrierOptions = useCarriersForSelect({ enabledOnSiteId: selectedSite?.id, valueKey: 'code' });

  const notAssignedLabel = t('sections.scheduling.fields.carrier.notAssigned');
  const carrierLabel = carrierOptions.find((option) => option.value === carrierCode)?.label ?? notAssignedLabel;

  // Kept as a closure (not a top-level helper) so the i18next-no-undefined-translation-keys
  // ESLint rule can resolve `t`'s namespace from the enclosing `useTranslation` call.
  function buildSchedulingSummaryItems(): StepperAccordionSummaryItem[] {
    const items: StepperAccordionSummaryItem[] = [];
    if (deliveryTimeSlot) {
      items.push({
        label: tCommon('fields.delivery'),
        value: formatSchedulingTimeSlotLabel(deliveryTimeSlot, i18n.language),
      });
    }
    items.push({ label: tCommon('fields.carrier'), value: carrierLabel });
    return items;
  }

  return <StepperAccordionSummary items={buildSchedulingSummaryItems()} />;
}
